import React, { useEffect, useState, useRef, useMemo } from 'react';
import { GetButtonColorClass } from './Utils';
import {
    Callout,
    DirectionalHint,
    FontWeights,
    makeStyles,
    useTheme,
    DefaultButton,
    IconButton,
    Calendar,
    DayOfWeek,
    DateRangeType,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import '../Styles/PeriodPicker.scss';
import { Period, UserReportingPeriodState } from '../api/generated/data-contracts';
import { useTranslation } from 'react-i18next';
import { getISOWeek } from 'date-fns';
import { usePeriodPickerTranslations } from '../hooks/usePriodPickerTranslations';
import { isAfterOrEqualDate, isBeforeOrEqualDate } from '../helpers/date';
import { useGridContext } from './Contexts/GridContext/context';
const getStyles = makeStyles(theme => ({
    callout: {
        maxWidth: 800,
        minWidth: 300,
    },
    backBtnCallout: {
        width: 330,
        padding: '6px 8px',
    },
    header: {
        padding: '18px 24px 12px',
        textAlign: 'center',
    },
    title: [
        theme.fonts.mediumPlus,
        {
            margin: 'auto',
            fontWeight: FontWeights.semilight,
        },
    ],
    inner: {
        height: '100%',
        padding: '0 24px 20px',
    },
}));

export const dayPickerStrings = {
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    goToToday: 'Go to today',
    weekNumberFormatString: 'Week number {0}',
    prevMonthAriaLabel: 'Previous month',
    nextMonthAriaLabel: 'Next month',
    prevYearAriaLabel: 'Previous year',
    nextYearAriaLabel: 'Next year',
    prevYearRangeAriaLabel: 'Previous year range',
    nextYearRangeAriaLabel: 'Next year range',
    closeButtonAriaLabel: 'Close',
};

const calendardaysformattetarray = [];
let calendarlastdayhovered;

interface PeriodPickerProps {
    defaultStartDate: Date;
    allPeriods: Period[];
    currentTimeState: UserReportingPeriodState;
    showRememberSubmitCallout: boolean;
    selectedDateUpdated: (date: Date) => void;
    currentPeriodIsLoading: boolean;
    setPeriodNotCreated: React.Dispatch<any>;
    parentSelectedDate: Date;
}

function PeriodPicker(props: PeriodPickerProps) {
    const { t } = useTranslation('timesheet');
    const DayPickerStrings = usePeriodPickerTranslations();
    const theme = useTheme();
    const styles = getStyles();
    const backButtonId = useId('backButton-callout');
    const [showRememberSubmitCallout, setShowRememberSubmitCallout] = useState<boolean>(props.showRememberSubmitCallout);
    const [rememberSubmitCalloutDismissed, setRememberSubmitCalloutDismissed] = useState<boolean>(!props.showRememberSubmitCallout);
    const [showFlyout, setShowFlyout] = useState<boolean>(false);
    const buttonFieldRef = useRef(null);
    const [selectedDate, setSelectedDate] = React.useState<Date>();
    const [mindate, setMindate] = React.useState<Date>(new Date());
    const [maxdate, setMaxdate] = React.useState<Date>(new Date());

    const [currentPeriodRealStart, setCurrentPeriodRealStart] = useState<Date>(new Date());
    const [currentPeriodRealEnd, setCurrentPeriodRealEnd] = useState<Date>(new Date());
    const { selectedTimePeriod } = useGridContext();

    const [currentTimeState, setCurrentTimeState] = useState<UserReportingPeriodState>(props.currentTimeState);

    const calref = useRef(null);

    useEffect(() => {
        const firstPeriod = props.allPeriods[0];
        const lastPeriod = props.allPeriods[props.allPeriods.length - 1];

        const startDate = new Date(firstPeriod.startDate);
        setMindate(startDate);

        const endDate = new Date(lastPeriod.endDate);
        endDate.setDate(endDate.getDate() + 7);
        setMaxdate(endDate);
    }, [props.allPeriods]);

    useEffect(() => {
        setSelectedDate(props.defaultStartDate);
    }, [props.defaultStartDate]);

    useEffect(() => {
        if (selectedDate != null && selectedDate !== undefined) {
            const currentPeriod = props.allPeriods.find(p => {
                const isAfter = isAfterOrEqualDate(selectedDate, new Date(p.startDate));
                const isBefore = isBeforeOrEqualDate(selectedDate, new Date(p.endDate));

                if (isAfter && isBefore) {
                    return true;
                }
            });

            if (!currentPeriod) {
                props.selectedDateUpdated(selectedDate);
                setCurrentPeriodRealStart(new Date(selectedDate));
                setCurrentPeriodRealEnd(new Date(selectedDate));
                props.setPeriodNotCreated(true);
            } else {
                setCurrentPeriodRealStart(new Date(currentPeriod.startDate));
                setCurrentPeriodRealEnd(new Date(currentPeriod.endDate));
                setRememberSubmitCalloutDismissed(!props.showRememberSubmitCallout);
                props.selectedDateUpdated(new Date(currentPeriod.startDate));
            }
        }
    }, [selectedDate]);

    useEffect(() => {
        setCurrentTimeState(props.currentTimeState);
    }, [props.currentTimeState]);

    useEffect(() => {
        setShowRememberSubmitCallout(props.showRememberSubmitCallout);
        setRememberSubmitCalloutDismissed(!props.showRememberSubmitCallout);
    }, [props.showRememberSubmitCallout]);

    const calendarbuttonClicked = () => {
        setShowFlyout(!showFlyout);
    };

    const onSelectDate = (date: Date): void => {
        const selectedPeriod = props.allPeriods.find(p => isAfterOrEqualDate(date, new Date(p.startDate)) && isBeforeOrEqualDate(date, new Date(p.endDate)));

        if (selectedPeriod) {
            setSelectedDate(new Date(selectedPeriod.startDate));
            setShowFlyout(false);
            setRememberSubmitCalloutDismissed(true);
            return; // To make it blink with the rest of the UI
        }
        setShowFlyout(false);
        setRememberSubmitCalloutDismissed(true);
        const newEndDate = new Date(selectedTimePeriod.endDate);
        newEndDate.setDate(newEndDate.getDate() + 1);
        setSelectedDate(newEndDate);
    };

    const nextPeriodClick = (): void => {
        onSelectDate(selectedTimePeriod.nextPeriodStartDate); // Use onSelectDate to switch to the period for next day after end of old period
    };

    const prevPeriodClick = (): void => {
        console.log('Period not found for selected date', selectedDate);
        const existingPeriodBeforeIndex = props.allPeriods.findIndex(p => p.id === selectedTimePeriod.periodId);

        const lastExistingPeriod = props.allPeriods[existingPeriodBeforeIndex - 1];
        // Use onSelectDate to switch to the period for the day before start of old period
        onSelectDate(new Date(lastExistingPeriod.endDate));
    };

    const flyoutDismissed = (ev: any): void => {
        setShowFlyout(false);
    };

    const highlightSelectedPeriod = (e: any) => {
        try {
            if (calref != null && calref.current != null) {
                if (
                    e.relatedTarget != null &&
                    (e.relatedTarget.getAttribute('role') === 'gridcell' || e.relatedTarget.parentNode.getAttribute('role') === 'gridcell')
                ) {
                    let caltd = null;
                    if (e.relatedTarget.nodeName === 'TD') {
                        caltd = e.relatedTarget;
                    } else if (e.relatedTarget.nodeName === 'BUTTON') {
                        caltd = e.relatedTarget.parentNode;
                    }
                    const button = caltd.children[0];
                    const buttonAreaLabel = button.getAttribute('aria-label');

                    if (button.classList === undefined || button.classList.contains('ms-DatePicker-day-button') === false) {
                        return;
                    }

                    const hovereddate = new Date(buttonAreaLabel);
                    const mouseoverdate = new Date(buttonAreaLabel).toString();

                    if (calendarlastdayhovered !== mouseoverdate) {
                        calendarlastdayhovered = mouseoverdate;
                        try {
                            for (let i = 0; i < calendardaysformattetarray.length; i++) {
                                calendardaysformattetarray[i].style.backgroundColor = '';
                            }
                        } catch (err) {
                            console.log('clean cal err', err);
                        }

                        const currentDayElement = calref.current._dayPicker.current.days[mouseoverdate];

                        currentDayElement.style.backgroundColor = theme.palette.neutralLight;
                        calendardaysformattetarray.push(currentDayElement);
                        const hoveredperiods = props.allPeriods.filter(p => new Date(p.startDate) <= hovereddate && new Date(p.endDate) >= hovereddate);
                        if (hoveredperiods.length >= 1) {
                            const hoveredperiod = hoveredperiods[0];

                            let diffTime = Math.abs(new Date(hoveredperiod.endDate).getTime() - hovereddate.getTime());
                            const daystillend = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                            let previuosDaytd = currentDayElement;
                            for (let i = 0; i < daystillend; i++) {
                                if (previuosDaytd.nextElementSibling != null) {
                                    previuosDaytd = previuosDaytd.nextElementSibling;
                                    previuosDaytd.style.backgroundColor = theme.palette.neutralLight;
                                    calendardaysformattetarray.push(previuosDaytd);
                                } else {
                                    if (previuosDaytd.parentNode.nextElementSibling != null && previuosDaytd.parentNode.nextElementSibling.nodeName === 'TR') {
                                        previuosDaytd = previuosDaytd.parentNode.nextElementSibling.firstChild;
                                        previuosDaytd.style.backgroundColor = theme.palette.neutralLight;
                                        calendardaysformattetarray.push(previuosDaytd);
                                    }
                                }
                            }
                            let nextDaytd = currentDayElement;
                            diffTime = Math.abs(new Date(hoveredperiod.startDate).getTime() - hovereddate.getTime());
                            const daysfromstart = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            for (let i = 0; i < daysfromstart; i++) {
                                if (nextDaytd.previousElementSibling != null) {
                                    nextDaytd = nextDaytd.previousElementSibling;
                                    nextDaytd.style.backgroundColor = theme.palette.neutralLight;
                                    calendardaysformattetarray.push(nextDaytd);
                                } else {
                                    if (nextDaytd.parentNode.previousElementSibling != null && nextDaytd.parentNode.previousElementSibling.nodeName === 'TR') {
                                        nextDaytd = nextDaytd.parentNode.previousElementSibling.lastChild;
                                        nextDaytd.style.backgroundColor = theme.palette.neutralLight;
                                        calendardaysformattetarray.push(nextDaytd);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } catch (err) {
            console.log('err', err);
        }
    };

    //WARNING: this is calculated every time on the fly and not from the periodName on the backend
    const periodName = useMemo(() => {
        const startMonthName = t(`common:shortMonths.${currentPeriodRealStart.getMonth()}`);
        const endMonthName = t(`common:shortMonths.${currentPeriodRealEnd.getMonth()}`);
        const weekNumber = getISOWeek(currentPeriodRealStart);
        return t('common:previewPeriodName', {
            startDate: currentPeriodRealStart.getDate(),
            startMonth: startMonthName,
            endDate: currentPeriodRealEnd.getDate(),
            endMonth: endMonthName,
            week: weekNumber,
        });
    }, [currentPeriodRealStart, currentPeriodRealEnd, t]);

    const periodBackButtonRef = useRef();

    if (props.currentPeriodIsLoading) {
        return <Spinner size={SpinnerSize.large} />;
    }

    return (
        <>
            <div id="tft-periodPicker" ref={buttonFieldRef} style={{ display: 'flex' }}>
                <IconButton
                    id={backButtonId}
                    iconProps={{ iconName: 'ChevronLeftSmall' }}
                    onClick={prevPeriodClick}
                    style={{ color: theme.semanticColors.bodyTextChecked }}
                    componentRef={ref => {
                        // if ((ref as any)?._buttonElement?.current) {
                        //     setPeriodBackButtonRef((ref as any)?._buttonElement?.current)
                        // }
                        periodBackButtonRef.current = (ref as any)?._buttonElement?.current;
                    }}
                />
                <DefaultButton
                    style={{ minWidth: 160 }}
                    text={periodName}
                    //   text={`${currentPeriodRealStart.getDate()} ${GetMonthNameShort(currentPeriodRealStart.getMonth())} - ${currentPeriodRealEnd.getDate()} ${GetMonthNameShort(currentPeriodRealEnd.getMonth())}`}
                    onClick={calendarbuttonClicked}
                    className={
                        'periodPickerButton ' +
                        GetButtonColorClass(
                            new Date(currentPeriodRealStart).setUTCTime(),
                            new Date(currentPeriodRealEnd).setUTCTime(),
                            new Date().setUTCTime(),
                            currentTimeState,
                        )
                    }
                    styles={{ root: { marginRight: theme.spacing.s1, marginLeft: theme.spacing.s1 } }}
                />
                <IconButton
                    iconProps={{ iconName: 'ChevronRightSmall' }}
                    onClick={nextPeriodClick}
                    // style={{marginLeft: '2%', width: 40, color: theme.semanticColors.bodyText, animation: 'fadein 1s'}}
                    style={{ color: theme.semanticColors.bodyTextChecked }}
                />
            </div>
            {!rememberSubmitCalloutDismissed && showRememberSubmitCallout && periodBackButtonRef.current && (
                <DelayedRender timeout={5000}>
                    <Callout
                        className={styles.backBtnCallout + ' rememberSubmit'}
                        target={periodBackButtonRef}
                        directionalHint={DirectionalHint.leftCenter}
                        styles={{
                            beakCurtain: { backgroundColor: '#f39494 !important' },
                            calloutMain: { backgroundColor: '#f39494 !important' },
                            beak: { backgroundColor: '#f39494 !important' },
                        }}
                    >
                        <IconButton
                            onClick={() => setRememberSubmitCalloutDismissed(true)}
                            iconProps={{ iconName: 'ChromeClose' }}
                            styles={{ root: { height: 18, width: 18 }, icon: { fontSize: 10 } }}
                        />
                        <span style={{ paddingLeft: 6, color: 'black' }}>{t('reminder')}</span>
                    </Callout>
                </DelayedRender>
            )}
            {showFlyout && (
                <Callout className={styles.callout} role="alertdialog" gapSpace={0} target={buttonFieldRef} onDismiss={flyoutDismissed} setInitialFocus>
                    <div
                        className={styles.inner}
                        onMouseLeave={() => {
                            calendarlastdayhovered = '';
                            try {
                                for (let i = 0; i < calendardaysformattetarray.length; i++) {
                                    calendardaysformattetarray[i].style.backgroundColor = '';
                                }
                            } catch (err) {
                                console.log('clean cal err', err);
                            }
                        }}
                    >
                        <Calendar
                            onSelectDate={onSelectDate}
                            componentRef={calref}
                            value={selectedDate!}
                            strings={DayPickerStrings}
                            isDayPickerVisible={true}
                            highlightSelectedMonth={true}
                            showMonthPickerAsOverlay={false}
                            showGoToToday={false}
                            dateRangeType={DateRangeType.Day}
                            firstDayOfWeek={DayOfWeek.Monday}
                            minDate={mindate}
                            maxDate={maxdate}
                            // onMouseMove={(e: any) => { highlightSelectedPeriod(e); }}
                            // onMouseOver={(e: any) => { highlightSelectedPeriod(e); }}
                            {...{
                                onMouseMove: (e: any) => {
                                    highlightSelectedPeriod(e);
                                },
                                onMouseOver: (e: any) => {
                                    highlightSelectedPeriod(e);
                                },
                            }}
                        />
                    </div>
                </Callout>
            )}
        </>
    );
}

export default PeriodPicker;

const DelayedRender = ({ children, timeout = 500 }: { children: React.ReactNode; timeout?: number }) => {
    const [go, setGo] = useState(false);
    useEffect(() => {
        if (!go) {
            const timer = setTimeout(() => setGo(true), timeout);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [go, timeout]);

    if (!go) return null;

    return <>{children}</>;
};
